import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  useGetList,
  useRecordContext,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
} from '@mui/material';
import {
  AccountTree as DiagramIcon,
  Factory as PlantIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import RolePermissionManager from '../../components/RolePermissionManager';
import { useRolePermissionEditTransform } from '../../hooks/useRolePermissionTransform';

const EditTitle = () => {
  const record = useRecordContext();
  console.log('🔍 DiagramsEdit - record completo:', record);
  console.log('🔍 DiagramsEdit - record.roles:', record?.roles);
  console.log('🔍 DiagramsEdit - record.roles tipo:', typeof record?.roles);
  console.log(
    '🔍 DiagramsEdit - record.roles es array:',
    Array.isArray(record?.roles)
  );
  return record ? `Editar Diagrama "${record.name}"` : 'Editar Diagrama';
};

const DiagramsEdit = () => {
  // Obtener lista de plantas
  const { data: plants, isLoading: plantsLoading } = useGetList('plants', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'plantName', order: 'ASC' },
  });

  const { transformForSave } = useRolePermissionEditTransform();

  const plantChoices = plants
    ? plants.map((plant: any) => ({
        id: plant.id,
        name: plant.plantName,
      }))
    : [];

  return (
    <Edit title={<EditTitle />} redirect='show' mutationMode='pessimistic' transform={transformForSave}>
      <Box sx={{ padding: 3, maxWidth: 1000, margin: '0 auto' }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardContent>
            <Box display='flex' alignItems='center' mb={3}>
              <DiagramIcon
                sx={{ mr: 2, fontSize: 32, color: 'primary.main' }}
              />
              <Box>
                <Typography variant='h5' component='h1' fontWeight='bold'>
                  Editar Diagrama
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Modifica la información del diagrama
                </Typography>
              </Box>
            </Box>

            <SimpleForm
              sx={{
                '& .RaSimpleForm-toolbar': {
                  marginTop: 3,
                  padding: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 2,
                },
              }}
            >
              <Grid container spacing={3}>
                {/* Información Principal */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <DiagramIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Información del Diagrama
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <TextInput
                        source='name'
                        label='Nombre del Diagrama'
                        validate={[required()]}
                        fullWidth
                        sx={{ mb: 2 }}
                        helperText='Nombre descriptivo para el diagrama'
                      />

                      <TextInput
                        source='description'
                        label='Descripción'
                        fullWidth
                        multiline
                        rows={3}
                        sx={{ mb: 2 }}
                        helperText='Descripción opcional del diagrama'
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Planta y Configuración */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <PlantIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Asignación
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <ReferenceInput
                        source='plantId'
                        reference='plants'
                        sort={{ field: 'plantName', order: 'ASC' }}
                      >
                        <SelectInput
                          optionText='plantName'
                          optionValue='id'
                          label='Planta'
                          validate={[required()]}
                          fullWidth
                          isLoading={plantsLoading}
                          helperText='Selecciona la planta a la que pertenece este diagrama'
                        />
                      </ReferenceInput>

                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: 'warning.light',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant='body2' color='warning.dark'>
                          ⚠️ Cambiar la planta puede afectar los nodos y
                          conexiones existentes del diagrama
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Gestión de Roles */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <GroupIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Roles de Acceso
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <RolePermissionManager
                        source="roles"
                        title="Gestión de Roles para Diagrama"
                        subtitle="Configura los roles que tendrán acceso a este diagrama y su nivel de permisos"
                        helperText="Selecciona roles y asigna niveles de permisos (READ, WRITE, ADMIN) para controlar el acceso a este diagrama"
                      />
                      <Box sx={{ mt: 2 }}>
                        <Typography variant='body2' color='text.secondary'>
                          Los roles determinan qué usuarios pueden ver y
                          gestionar este diagrama. Puedes agregar múltiples
                          roles según sea necesario.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </SimpleForm>
          </CardContent>
        </Card>
      </Box>
    </Edit>
  );
};

export default DiagramsEdit;
